import React, { useEffect, useMemo, useState } from "react";
import { parseUnits } from "ethers";

import { api } from "../../lib/api";
import {
    preflightCustomDeployment,
    prepareCustomDeployment,
    confirmCustomDeployment,
    requestCustomVerification,
    checkCustomVerificationStatus,
} from "../../lib/customContractsApi";
import {
    getCurrentAddress,
    switchActiveWalletChain,
    sendActiveWalletTransaction,
} from "../../wallet/walletClient";
import { Link } from "react-router-dom";

function clean(value) {
    return String(value || "").trim();
}

function isEvmAddress(value) {
    return typeof value === "string" && /^0x[a-fA-F0-9]{40}$/.test(value.trim());
}

const WALLET_CONFIRM_TIMEOUT_MS = 300000;

function withTimeout(promise, message) {
    let timeoutId;

    const timeout = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => {
            reject(new Error(message));
        }, WALLET_CONFIRM_TIMEOUT_MS);
    });

    return Promise.race([promise, timeout]).finally(() => {
        window.clearTimeout(timeoutId);
    });
}

function sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function renderSafeText(value, fallback = "-") {
    if (value === null || value === undefined || value === "") return fallback;

    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "bigint") return value.toString();

    if (Array.isArray(value)) {
        if (value.length === 0) return fallback;
        return value
            .map((item) => renderSafeText(item, ""))
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    }

    return fallback;
}

function safeError(err, fallback = "Something went wrong.") {
    const data = err?.response?.data;

    const value =
        data?.message ||
        data?.error ||
        data?.details?.message ||
        data?.details?.error ||
        data?.reason ||
        err?.message ||
        err?.shortMessage ||
        fallback;

    const text = renderSafeText(value, fallback);

    if (text === "{}" || text === "[]") {
        try {
            return JSON.stringify(data || err, null, 2);
        } catch {
            return fallback;
        }
    }

    return text;
}

function normalizeBps(value) {
    const raw = clean(value || "0");

    if (!/^[0-9]+$/.test(raw)) return "0";

    const n = Number(raw);

    if (!Number.isInteger(n) || n < 0) return "0";
    if (n > 1000) return "1000";

    return String(n);
}

function normalizeDecimals(value) {
    const raw = clean(value || "18");

    if (!/^[0-9]+$/.test(raw)) return 18;

    const n = Number(raw);

    if (!Number.isInteger(n) || n < 0 || n > 18) return 18;

    return n;
}

function getCustomConstructorInputs(compileResult) {
    const abi =
        Array.isArray(compileResult?.abi)
            ? compileResult.abi
            : Array.isArray(compileResult?.artifact?.abi)
                ? compileResult.artifact.abi
                : Array.isArray(compileResult?.compileMeta?.abi)
                    ? compileResult.compileMeta.abi
                    : [];

    const constructorItem = abi.find((item) => item?.type === "constructor");

    if (!constructorItem) return [];

    return Array.isArray(constructorItem.inputs) ? constructorItem.inputs : [];
}

function hasKnownCustomConstructorShape(compileResult) {
    const abi =
        Array.isArray(compileResult?.abi)
            ? compileResult.abi
            : Array.isArray(compileResult?.artifact?.abi)
                ? compileResult.artifact.abi
                : Array.isArray(compileResult?.compileMeta?.abi)
                    ? compileResult.compileMeta.abi
                    : [];

    return abi.some((item) => item?.type === "constructor");
}

function getSourceConstructorShape(source) {
    const text = String(source || "")
        .replace(/\/\*[\s\S]*?\*\//g, "")
        .replace(/\/\/.*$/gm, "");

    const match = text.match(/\bconstructor\s*\(([\s\S]*?)\)/);

    if (!match) {
        return {
            known: true,
            count: 0,
            params: [],
        };
    }

    const rawParams = String(match[1] || "").trim();

    if (!rawParams) {
        return {
            known: true,
            count: 0,
            params: [],
        };
    }

    const params = rawParams
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

    return {
        known: true,
        count: params.length,
        params,
    };
}

function buildRecommendedConstructorArgs({
    tokenType,
    walletAddress,
    starterSeed,
    compileResult,
    source,
}) {
    const cleanType = clean(tokenType).toUpperCase();
    const wallet = clean(walletAddress);
    const customConstructorInputs = getCustomConstructorInputs(compileResult);
    const sourceConstructorShape = getSourceConstructorShape(source);

    if (hasKnownCustomConstructorShape(compileResult)) {
        if (customConstructorInputs.length === 0) {
            return [];
        }
    }

    if (sourceConstructorShape.known && sourceConstructorShape.count === 0) {
        return [];
    }

    if (!isEvmAddress(wallet)) {
        return [];
    }

    if (cleanType === "ERC721") {
        const baseURI = clean(starterSeed?.baseURI) || "ipfs://YOUR_METADATA_CID/";
        const royaltyBps = normalizeBps(starterSeed?.royaltyFeeNumerator);
        const royaltyReceiver = isEvmAddress(starterSeed?.royaltyReceiver)
            ? clean(starterSeed.royaltyReceiver)
            : wallet;

        if (Number(royaltyBps) > 0) {
            return [wallet, baseURI, royaltyReceiver, royaltyBps];
        }

        return [wallet, baseURI];
    }

    if (cleanType === "ERC1155") {
        const uri = clean(starterSeed?.baseURI) || "ipfs://YOUR_METADATA_CID/{id}.json";
        const royaltyBps = normalizeBps(starterSeed?.royaltyFeeNumerator);
        const royaltyReceiver = isEvmAddress(starterSeed?.royaltyReceiver)
            ? clean(starterSeed.royaltyReceiver)
            : wallet;

        if (Number(royaltyBps) > 0) {
            return [wallet, uri, royaltyReceiver, royaltyBps];
        }

        return [wallet, uri];
    }

    const decimals = normalizeDecimals(starterSeed?.erc20Decimals);
    const humanSupply = clean(starterSeed?.erc20InitialSupply || "0") || "0";

    let rawSupply = "0";

    try {
        rawSupply = parseUnits(humanSupply, decimals).toString();
    } catch {
        rawSupply = "0";
    }

    return [wallet, rawSupply];
}

function formatGasUnits(value) {
    const raw = String(value || "").trim();

    if (!/^[0-9]+$/.test(raw)) return "-";

    return Number(raw).toLocaleString();
}

function formatNativeRange(gas) {
    const low = gas?.estimatedCostNativeLow;
    const market = gas?.estimatedCostNativeMarket;
    const aggressive = gas?.estimatedCostNativeAggressive;
    const symbol = gas?.nativeSymbol || "native";

    if (low && aggressive) {
        return `~${low} – ${aggressive} ${symbol}`;
    }

    if (market) {
        return `~${market} ${symbol}`;
    }

    return "Unavailable";
}

function formatUsdRange(gas) {
    if (gas?.isTestnet) {
        return "No USD value for testnet gas";
    }

    const low = gas?.estimatedCostUsdLow;
    const aggressive = gas?.estimatedCostUsdAggressive;

    if (low && aggressive) {
        return `~$${low} – $${aggressive}`;
    }

    if (gas?.estimatedCostUsdMarket) {
        return `~$${gas.estimatedCostUsdMarket}`;
    }

    return "USD estimate unavailable";
}

function describeConstructorArgs(args) {
    if (!Array.isArray(args) || args.length === 0) {
        return "No constructor arguments required.";
    }

    return `${args.length} constructor argument${args.length === 1 ? "" : "s"} configured.`;
}

function getCompileDisplayType(result) {
    return (
        result?.displayType ||
        result?.capabilities?.displayType ||
        "Custom Contract"
    );
}

function getCompileStarterTemplate(result, fallback) {
    return (
        result?.starterTemplate ||
        result?.capabilities?.starterTemplate ||
        fallback ||
        "CUSTOM"
    );
}

function getCompileCapabilityLabels(result) {
    const labels =
        result?.capabilityLabels ||
        result?.capabilities?.capabilityLabels ||
        [];

    return Array.isArray(labels) ? labels.filter(Boolean) : [];
}

function JsonPreview({ value }) {
    return (
        <pre className="max-h-52 overflow-auto rounded-xl bg-slate-950 p-3 text-xs text-slate-100">
            {JSON.stringify(value, null, 2)}
        </pre>
    );
}

const NETWORK_META = {
    ethereum: {
        logo: "/logos/ethereum.png",
        tone: "from-slate-100 to-slate-50",
    },
    sepolia: {
        logo: "/logos/ethereum.png",
        tone: "from-indigo-50 to-sky-50",
    },
    polygon: {
        logo: "/logos/polygon.png",
        tone: "from-purple-50 to-indigo-50",
    },
    arbitrum: {
        logo: "/logos/arbitrum.png",
        tone: "from-sky-50 to-blue-50",
    },
    avalanche: {
        logo: "/logos/avalanche.png",
        tone: "from-rose-50 to-red-50",
    },
    bnb: {
        logo: "/logos/bnb.png",
        tone: "from-amber-50 to-yellow-50",
    },
    base: {
        logo: "/logos/base.png",
        tone: "from-blue-50 to-cyan-50",
    },
    optimism: {
        logo: "/logos/optimism.png",
        tone: "from-rose-50 to-orange-50",
    },
};

function getNetworkMeta(network) {
    const key = String(network?.key || network?.label || network?.name || "")
        .trim()
        .toLowerCase();

    if (key.includes("arbitrum")) return NETWORK_META.arbitrum;
    if (key.includes("avalanche") || key.includes("fuji")) return NETWORK_META.avalanche;
    if (key.includes("polygon") || key.includes("amoy")) return NETWORK_META.polygon;
    if (key.includes("bnb") || key.includes("bsc")) return NETWORK_META.bnb;
    if (key.includes("base")) return NETWORK_META.base;
    if (key.includes("optimism") || key.includes("op ")) return NETWORK_META.optimism;
    if (key.includes("ethereum") || key.includes("sepolia")) return NETWORK_META.ethereum;

    return {
        logo: "",
        tone: "from-slate-50 to-white",
    };
}

function isTestnetNetwork(item) {
    const value = String(item?.label || item?.name || item?.key || "")
        .trim()
        .toLowerCase();

    return (
        value.includes("testnet") ||
        value.includes("sepolia") ||
        value.includes("fuji") ||
        value.includes("amoy")
    );
}

function NetworkLogo({ network }) {
    const meta = getNetworkMeta(network);

    if (meta.logo) {
        return (
            <div
                className={`flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-gradient-to-br ${meta.tone} shadow-sm`}
            >
                <img
                    src={meta.logo}
                    alt=""
                    className="h-8 w-8 object-contain"
                    loading="lazy"
                />
            </div>
        );
    }

    return (
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-xs font-bold text-slate-700 shadow-sm">
            NET
        </div>
    );
}

export default function CustomDeployPanel({
    compileResult,
    projectId,
    tokenType,
    starterSeed,
    source = "",
    isAuthed,
    restoreLoading,
    usageSnapshot = null,
    accountUsageLoading = false,
    onUsageRefresh = null,
    onDeploymentConfirmed,
}) {
    const [networks, setNetworks] = useState([]);
    const [network, setNetwork] = useState("");
    const [networksLoading, setNetworksLoading] = useState(false);
    const [networkError, setNetworkError] = useState("");
    const [networkTab, setNetworkTab] = useState("testnets");

    const [constructorArgsText, setConstructorArgsText] = useState("[]");
    const [argsTouched, setArgsTouched] = useState(false);

    const [preflightLoading, setPreflightLoading] = useState(false);
    const [preflightError, setPreflightError] = useState("");
    const [preflightStatus, setPreflightStatus] = useState("");
    const [preflightResult, setPreflightResult] = useState(null);

    const [deployLoading, setDeployLoading] = useState(false);
    const [deployStatus, setDeployStatus] = useState("");
    const [deployError, setDeployError] = useState("");
    const [deployResult, setDeployResult] = useState(null);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [verifyStatus, setVerifyStatus] = useState("");
    const [verifyError, setVerifyError] = useState("");
    const [verifyResult, setVerifyResult] = useState(null);
    function resetDeployState() {
        setPreflightLoading(false);
        setPreflightError("");
        setPreflightStatus("");
        setPreflightResult(null);

        setDeployLoading(false);
        setDeployStatus("");
        setDeployError("");
        setDeployResult(null);

        setVerifyLoading(false);
        setVerifyStatus("");
        setVerifyError("");
        setVerifyResult(null);
    }

    const walletAddress = getCurrentAddress?.() || "";

    const customVersionId =
        compileResult?.customVersionId ||
        compileResult?.versionId ||
        compileResult?.compileMeta?.customVersionId ||
        "";

    const deployBlocked = Boolean(compileResult?.deployBlocked);

    const deploymentLimitReached = Boolean(usageSnapshot?.deploymentLimitReached);
    const preflightLimitReached = Boolean(usageSnapshot?.preflightLimitReached);

    const deploymentLimitMessage =
        usageSnapshot?.deploymentsLimit >= 0
            ? `Deployment limit reached. Your ${usageSnapshot.plan} plan allows ${usageSnapshot.deploymentsLimit} total standard or custom deployments.`
            : "Deployment limit reached.";

    const preflightLimitMessage =
        usageSnapshot?.preflightsLimit >= 0
            ? `Preflight limit reached. Your ${usageSnapshot.plan} plan allows ${usageSnapshot.preflightsLimit} total standard or custom preflight simulations.`
            : "Preflight limit reached.";

    const preflightUsageText =
        usageSnapshot?.preflightsText ||
        (usageSnapshot?.preflightsLimit >= 0
            ? `${usageSnapshot?.preflightsUsed || 0} / ${usageSnapshot.preflightsLimit}`
            : "Usage unavailable");

    const deploymentUsageText =
        usageSnapshot?.deploymentsText ||
        (usageSnapshot?.deploymentsLimit >= 0
            ? `${usageSnapshot?.deploymentsUsed || 0} / ${usageSnapshot.deploymentsLimit}`
            : "Usage unavailable");

    const preflightCreditNotice =
        usageSnapshot?.preflightsLimit < 0
            ? "Your current plan includes unlimited preflight checks."
            : `This check uses 1 preflight credit. Current usage: ${preflightUsageText}.`;

    const deployCreditNotice =
        usageSnapshot?.deploymentsLimit < 0
            ? "Deployment is recorded only after your wallet transaction is confirmed on-chain."
            : `This uses 1 deployment credit only after the contract is confirmed on-chain. Current usage: ${deploymentUsageText}.`;

    const preflightOk = Boolean(
        preflightResult?.ok &&
        preflightResult?.preflight === true &&
        preflightResult?.network === network &&
        preflightResult?.projectId === projectId &&
        preflightResult?.customVersionId === customVersionId,
    );

    const selectedNetwork = useMemo(() => {
        return networks.find((item) => item.key === network) || null;
    }, [networks, network]);
    const mainnetNetworks = useMemo(() => {
        return networks.filter((item) => !isTestnetNetwork(item));
    }, [networks]);

    const testnetNetworks = useMemo(() => {
        return networks.filter((item) => isTestnetNetwork(item));
    }, [networks]);

    const visibleNetworks = networkTab === "mainnets" ? mainnetNetworks : testnetNetworks;

    const recommendedArgs = useMemo(() => {
        return buildRecommendedConstructorArgs({
            tokenType,
            walletAddress,
            starterSeed,
            compileResult,
            source,
        });
    }, [tokenType, walletAddress, starterSeed, compileResult, source]);

    useEffect(() => {
        let mounted = true;

        async function loadNetworks() {
            setNetworksLoading(true);
            setNetworkError("");

            try {
                const res = await api.get("/networks", {
                    params: {
                        includeTestnets: true,
                        onlyConfigured: true,
                    },
                });

                const list = Array.isArray(res?.data?.networks)
                    ? res.data.networks
                    : [];

                if (!mounted) return;

                setNetworks(list);

                // Do not auto-select any network.
                // User must explicitly choose a network before deployment.
            } catch (err) {
                if (!mounted) return;
                setNetworkError(safeError(err, "Failed to load networks."));
            } finally {
                if (mounted) setNetworksLoading(false);
            }
        }

        loadNetworks();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        setArgsTouched(false);
        setConstructorArgsText(JSON.stringify(recommendedArgs, null, 2));

        setPreflightResult(null);
        setPreflightStatus("");
        setPreflightError("");

        setDeployStatus("");
        setDeployError("");
        setDeployResult(null);
    }, [customVersionId, recommendedArgs]);

    useEffect(() => {
        if (argsTouched) return;

        setConstructorArgsText(JSON.stringify(recommendedArgs, null, 2));
    }, [recommendedArgs, argsTouched]);

    const constructorShape = useMemo(() => {
        const abiInputs = getCustomConstructorInputs(compileResult);

        if (hasKnownCustomConstructorShape(compileResult)) {
            return {
                known: true,
                count: abiInputs.length,
            };
        }

        return getSourceConstructorShape(source);
    }, [compileResult, source]);

    function resetRecommendedArgs() {
        setArgsTouched(false);
        setConstructorArgsText(JSON.stringify(recommendedArgs, null, 2));

        setPreflightResult(null);
        setPreflightStatus("");
        setPreflightError("");

        setDeployStatus("");
        setDeployError("");
        setDeployResult(null);
    }

    function parseConstructorArgs() {
        let parsed;

        try {
            parsed = JSON.parse(constructorArgsText);
        } catch {
            throw new Error("Constructor args must be valid JSON.");
        }

        if (!Array.isArray(parsed)) {
            throw new Error("Constructor args must be a JSON array.");
        }

        return parsed;
    }

    async function handlePreflightCustom() {
        setPreflightError("");
        setPreflightStatus("");
        setPreflightResult(null);

        setDeployError("");
        setDeployStatus("");
        setDeployResult(null);

        if (restoreLoading) {
            setPreflightError("Checking your session. Please wait a moment.");
            return;
        }

        if (!isAuthed) {
            setPreflightError("Please connect wallet and sign in from the top bar before preflight.");
            return;
        }

        if (accountUsageLoading) {
            setPreflightError("Checking your usage limits. Please wait a moment.");
            return;
        }

        if (preflightLimitReached) {
            setPreflightError(preflightLimitMessage);
            return;
        }

        const activeWallet = clean(getCurrentAddress?.());

        if (!isEvmAddress(activeWallet)) {
            setPreflightError("Please connect wallet from the top bar before preflight.");
            return;
        }

        if (!customVersionId) {
            setPreflightError("Compile result is missing customVersionId. Compile again.");
            return;
        }

        if (!projectId) {
            setPreflightError("Project ID is missing.");
            return;
        }

        if (!network) {
            setPreflightError("Select a deployment network before preflight.");
            return;
        }

        if (deployBlocked) {
            setPreflightError("Deployment is blocked by security warnings. Fix the source and compile again.");
            return;
        }

        let constructorArgs;

        try {
            constructorArgs = parseConstructorArgs();
        } catch (err) {
            setPreflightError(err.message);
            return;
        }

        if (
            constructorShape.known &&
            constructorArgs.length !== constructorShape.count
        ) {
            setPreflightError(
                `Constructor argument mismatch: this compiled contract expects ${constructorShape.count}, but your JSON has ${constructorArgs.length}. ${constructorShape.count === 0
                    ? "Click Use empty args."
                    : "Update the constructor args JSON."
                }`,
            );
            return;
        }


        setPreflightLoading(true);

        let preflightRequestSent = false;

        try {
            setPreflightStatus("Running safety preflight… This may use 1 preflight credit.");

            preflightRequestSent = true;

            const result = await preflightCustomDeployment({
                network,
                projectId,
                customVersionId,
                constructorArgs,
                userAddress: activeWallet,
            });

            if (!result?.ok) {
                throw new Error(
                    renderSafeText(
                        result?.message || result?.error,
                        "Custom deployment preflight failed.",
                    ),
                );
            }

            setPreflightResult(result);
            setPreflightStatus("Preflight passed ✅ Deployment is ready for wallet signing.");
        } catch (err) {
            setPreflightError(safeError(err, "Custom preflight failed."));
        } finally {
            setPreflightLoading(false);

            if (preflightRequestSent) {
                await onUsageRefresh?.();
            }
        }
    }

    async function handleDeployCustom() {
        setDeployError("");
        setDeployStatus("");
        setDeployResult(null);

        if (restoreLoading) {
            setDeployError("Checking your session. Please wait a moment.");
            return;
        }

        if (!isAuthed) {
            setDeployError("Please connect wallet and sign in from the top bar before deploying.");
            return;
        }

        if (accountUsageLoading) {
            setDeployError("Checking your usage limits. Please wait a moment.");
            return;
        }

        if (deploymentLimitReached) {
            setDeployError(deploymentLimitMessage);
            return;
        }

        const activeWallet = clean(getCurrentAddress?.());

        if (!isEvmAddress(activeWallet)) {
            setDeployError("Please connect wallet from the top bar before deploying.");
            return;
        }

        if (!customVersionId) {
            setDeployError("Compile result is missing customVersionId. Compile again.");
            return;
        }

        if (!projectId) {
            setDeployError("Project ID is missing.");
            return;
        }

        if (!network) {
            setDeployError("Select a deployment network.");
            return;
        }

        if (deployBlocked) {
            setDeployError("Deployment is blocked by security warnings. Fix the source and compile again.");
            return;
        }

        let constructorArgs;

        try {
            constructorArgs = parseConstructorArgs();
        } catch (err) {
            setDeployError(err.message);
            return;
        }

        if (
            constructorShape.known &&
            constructorArgs.length !== constructorShape.count
        ) {
            setDeployError(
                `Constructor argument mismatch: this compiled contract expects ${constructorShape.count}, but your JSON has ${constructorArgs.length}. ${constructorShape.count === 0
                    ? "Click Use empty args."
                    : "Update the constructor args JSON."
                }`,
            );
            return;
        }

        setDeployLoading(true);

        try {
            setDeployStatus("Preparing custom deployment transaction…");

            const prep = await prepareCustomDeployment({
                network,
                projectId,
                customVersionId,
                constructorArgs,
                userAddress: activeWallet,
            });

            if (!prep?.ok || !prep?.tx?.data || !prep?.requestId) {
                throw new Error("Backend did not return a valid custom deployment transaction.");
            }

            const chainId = Number(prep.chainId || selectedNetwork?.chainId || 0);

            if (!chainId) {
                throw new Error("Missing chainId for selected network.");
            }

            setDeployStatus(`Switching wallet to ${prep.network || network}…`);
            await withTimeout(
                switchActiveWalletChain(chainId),
                "Wallet network switch timed out. Open your wallet app or browser extension, close any pending request, then try again.",
            );

            setDeployStatus("Confirm custom deployment in your wallet…");

            const txHash = await withTimeout(
                sendActiveWalletTransaction(prep.tx, {
                    chainId,
                    expectedAddress: activeWallet,
                }),
                "Wallet confirmation timed out or popup was closed. No transaction was confirmed through Krionex.",
            );

            setDeployStatus(`Transaction sent (${txHash.slice(0, 10)}…). Waiting for on-chain confirmation… This can take a minute on busy testnets.`);

            const confirmed = await confirmCustomDeployment({
                requestId: prep.requestId,
                network: prep.network || network,
                txHash,
                userAddress: activeWallet,
                confirmations: selectedNetwork?.confirmations || 1,
            });

            setDeployResult(confirmed);
            setDeployStatus("Custom deployment confirmed ✅");

            // Show deployment in history immediately.
            onDeploymentConfirmed?.(confirmed);

            try {
                setVerifyStatus("Submitting verification automatically…");

                const verificationResult = await requestVerificationForDeployment(confirmed);

                if (verificationResult?.ok) {
                    onDeploymentConfirmed?.({
                        ...confirmed,
                        verification: verificationResult.verification || confirmed.verification,
                        verificationStatus:
                            verificationResult?.verification?.status ||
                            verificationResult?.status ||
                            confirmed.verificationStatus,
                        verificationMessage:
                            verificationResult?.verification?.message ||
                            verificationResult?.message ||
                            confirmed.verificationMessage,
                        verifiedAt:
                            verificationResult?.verification?.verifiedAt ||
                            verificationResult?.verifiedAt ||
                            confirmed.verifiedAt,
                    });
                }
            } catch (verifyErr) {
                setVerifyError(
                    safeError(
                        verifyErr,
                        "Deployment succeeded, but automatic verification could not be started.",
                    ),
                );
            }
        } catch (err) {
            setDeployError(safeError(err, "Custom deployment failed."));
        } finally {
            setDeployLoading(false);
        }
    }

    function getVerificationTarget() {
        const target = deployResult || {};

        return {
            deploymentFile: target.deploymentFile || "",
            requestId: target.requestId || "",
            txHash: target.txHash || "",
        };
    }
    function isVerifiedResponse(result) {
        const status = String(
            result?.verification?.status ||
            result?.status ||
            "",
        )
            .trim()
            .toLowerCase();

        const text = [
            result?.verification?.message,
            result?.message,
            result?.explorer?.result,
            result?.explorer?.message,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return (
            status === "verified" ||
            text.includes("already verified") ||
            text.includes("pass - verified") ||
            text.includes("successfully verified")
        );
    }

    function getVerificationDisplayMessage(result, fallback = "Verification status updated.") {
        if (isVerifiedResponse(result)) {
            return "Contract verified successfully ✅";
        }

        return renderSafeText(
            result?.verification?.message ||
            result?.message ||
            result?.verification?.status ||
            result?.status,
            fallback,
        );
    }
    async function requestVerificationForDeployment(targetDeployment) {
        const target = targetDeployment || {};

        const verificationTarget = {
            deploymentFile: target.deploymentFile || "",
            requestId: target.requestId || "",
            txHash: target.txHash || "",
        };

        if (
            !verificationTarget.deploymentFile &&
            !verificationTarget.requestId &&
            !verificationTarget.txHash
        ) {
            throw new Error("Deployment reference missing for verification.");
        }

        const submitted = await requestCustomVerification(verificationTarget);

        setVerifyResult(submitted);

        if (!submitted?.ok) {
            setVerifyError(
                renderSafeText(
                    submitted?.verification?.message ||
                    submitted?.message ||
                    submitted?.error,
                    "Automatic verification request failed.",
                ),
            );
            setVerifyStatus("");
            return submitted;
        }

        if (isVerifiedResponse(submitted)) {
            setVerifyError("");
            setVerifyStatus("Contract verified successfully ✅");
            return submitted;
        }

        setVerifyError("");
        setVerifyStatus("Verification submitted. Waiting for explorer confirmation…");

        let latest = submitted;

        // Limited polling only. This protects explorer quota and avoids unnecessary refreshes.
        const delays = [10000, 25000, 45000];

        for (let index = 0; index < delays.length; index += 1) {
            await sleep(delays[index]);

            let checked;

            try {
                checked = await checkCustomVerificationStatus(verificationTarget);
            } catch (err) {
                latest = {
                    ...latest,
                    pollError: safeError(err, "Verification status check failed."),
                };

                setVerifyStatus(
                    "Verification was submitted. Explorer confirmation may take a few minutes.",
                );

                continue;
            }

            latest = checked || latest;
            setVerifyResult(latest);

            if (isVerifiedResponse(latest)) {
                setVerifyError("");
                setVerifyStatus("Contract verified successfully ✅");
                return latest;
            }

            const statusText = String(
                latest?.verification?.status ||
                latest?.status ||
                latest?.message ||
                latest?.verification?.message ||
                "",
            ).toLowerCase();

            if (
                latest?.ok === false &&
                !statusText.includes("pending") &&
                !statusText.includes("queue") &&
                !statusText.includes("submitted") &&
                !statusText.includes("already submitted")
            ) {
                setVerifyError(
                    renderSafeText(
                        latest?.verification?.message ||
                        latest?.message ||
                        latest?.error,
                        "Verification could not be completed automatically.",
                    ),
                );
                setVerifyStatus("");
                return latest;
            }

            setVerifyStatus(
                `Verification submitted. Checking explorer status ${index + 1}/${delays.length}…`,
            );
        }

        setVerifyStatus(
            "Verification submitted. It may take a few minutes to appear in deployment history.",
        );

        return latest;
    }

    async function handleRequestVerification() {
        setVerifyError("");
        setVerifyStatus("");
        setVerifyResult(null);

        const target = getVerificationTarget();

        if (!target.deploymentFile && !target.requestId && !target.txHash) {
            setVerifyError("Deployment reference missing. Deploy again or reload deployment history.");
            return;
        }

        setVerifyLoading(true);

        try {
            setVerifyStatus("Submitting source verification using stored compiler input…");

            const result = await requestCustomVerification(target);

            setVerifyResult(result);

            if (!result?.ok) {
                setVerifyError(
                    renderSafeText(
                        result?.message ||
                        result?.error ||
                        result?.details?.message ||
                        result?.details?.error ||
                        result?.code,
                        "Verification request was rejected by the backend.",
                    ),
                );
                setVerifyStatus("");
                return;
            }

            setVerifyStatus(
                isVerifiedResponse(result)
                    ? "Contract verified successfully ✅"
                    : getVerificationDisplayMessage(
                        result,
                        "Verification submitted. Check status in a few seconds.",
                    ),
            );
        } catch (err) {
            setVerifyError(safeError(err, "Custom verification request failed."));
        } finally {
            setVerifyLoading(false);
        }
    }

    async function handleCheckVerificationStatus() {
        setVerifyError("");

        const target = getVerificationTarget();

        if (!target.deploymentFile && !target.requestId && !target.txHash) {
            setVerifyError("Deployment reference missing. Deploy again or reload deployment history.");
            return;
        }

        setVerifyLoading(true);

        try {
            setVerifyStatus("Checking verification status…");

            const result = await checkCustomVerificationStatus(target);

            setVerifyResult(result);

            if (!result?.ok) {
                setVerifyError(
                    result?.message ||
                    result?.error ||
                    result?.details?.message ||
                    result?.details?.error ||
                    result?.code ||
                    "Verification status check was rejected by the backend.",
                );
                setVerifyStatus("");
                return;
            }

            setVerifyStatus(
                isVerifiedResponse(result)
                    ? "Contract verified successfully ✅"
                    : getVerificationDisplayMessage(result, "Verification still pending."),
            );
        } catch (err) {
            setVerifyError(safeError(err, "Custom verification status check failed."));
        } finally {
            setVerifyLoading(false);
        }
    }

    if (!compileResult) {
        return null;
    }

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-slate-950">
                        Deploy Custom Contract
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                        Deploy the compiled custom version with your wallet. Backend prepares
                        the transaction; your wallet signs it.
                    </p>
                </div>

                <div
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${deployBlocked
                        ? "bg-red-100 text-red-700"
                        : "bg-emerald-100 text-emerald-700"
                        }`}
                >
                    {deployBlocked ? "Deploy Blocked" : "Ready after review"}
                </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
                <div>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-[#050713]">
                                    Deployment Network
                                </h3>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Choose exactly one chain for this custom contract deployment.
                                </p>
                            </div>
                        </div>

                        <div className="rounded-[1.5rem] border border-slate-200 bg-[#F8FAFC] p-4 shadow-[0_12px_35px_rgba(15,23,42,0.04)]">
                            <div className="grid rounded-2xl bg-slate-100 p-1 text-sm font-semibold text-slate-600 sm:grid-cols-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNetworkTab("testnets");
                                        setNetwork("");
                                        resetDeployState();
                                    }}
                                    disabled={deployLoading}
                                    className={`rounded-xl px-4 py-2 transition ${networkTab === "testnets"
                                        ? "bg-white text-[#050713] shadow-sm"
                                        : "hover:text-[#050713]"
                                        }`}
                                >
                                    Testnets
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setNetworkTab("mainnets");
                                        setNetwork("");
                                        resetDeployState();
                                    }}
                                    disabled={deployLoading}
                                    className={`rounded-xl px-4 py-2 transition ${networkTab === "mainnets"
                                        ? "bg-white text-[#050713] shadow-sm"
                                        : "hover:text-[#050713]"
                                        }`}
                                >
                                    Mainnets
                                </button>
                            </div>

                            <div className="mt-4 grid max-h-[520px] gap-3 overflow-y-auto pr-1">
                                {networksLoading ? (
                                    <p className="text-sm text-slate-600">
                                        Loading deployment networks…
                                    </p>
                                ) : null}

                                {!networksLoading && visibleNetworks.length === 0 ? (
                                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-800">
                                        No networks available in this group.
                                    </div>
                                ) : null}

                                {visibleNetworks.map((item) => {
                                    const isSelected = network === item.key;
                                    const isMainnet = !isTestnetNetwork(item);

                                    return (
                                        <button
                                            key={item.key}
                                            type="button"
                                            onClick={() => {
                                                setNetwork(item.key);
                                                resetDeployState();
                                            }}
                                            disabled={deployLoading}
                                            className={`group flex items-center justify-between gap-4 rounded-[1.35rem] border bg-white p-4 text-left shadow-sm transition ${isSelected
                                                ? "border-[#050713] ring-2 ring-[#050713]/10"
                                                : "border-slate-200 hover:-translate-y-0.5 hover:border-[#E8D7AD] hover:shadow-[0_14px_34px_rgba(15,23,42,0.08)]"
                                                }`}
                                        >
                                            <div className="flex min-w-0 items-center gap-4">
                                                <NetworkLogo network={item} />

                                                <div className="min-w-0">
                                                    <div className="truncate text-base font-semibold tracking-[-0.02em] text-[#050713]">
                                                        {renderSafeText(item.label || item.name || item.key)}
                                                    </div>

                                                    <div className="mt-1 text-sm text-slate-500">
                                                        Chain ID {renderSafeText(item.chainId)}
                                                    </div>

                                                    <span
                                                        className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${isMainnet
                                                            ? "border-red-200 bg-red-50 text-red-700"
                                                            : "border-emerald-200 bg-emerald-50 text-emerald-700"
                                                            }`}
                                                    >
                                                        {isMainnet ? "Mainnet" : "Testnet"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div
                                                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${isSelected
                                                    ? "border-[#050713] bg-[#050713]"
                                                    : "border-slate-300 bg-white"
                                                    }`}
                                            >
                                                {isSelected ? (
                                                    <span className="h-2.5 w-2.5 rounded-full bg-white" />
                                                ) : null}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {networkError ? (
                        <p className="mt-2 text-xs font-semibold text-red-700">
                            {renderSafeText(networkError)}
                        </p>
                    ) : null}
                    {!network ? (
                        <p className="mt-2 text-xs font-semibold text-amber-700">
                            Select a deployment network before preparing wallet deployment.
                        </p>
                    ) : null}

                    {selectedNetwork && !isTestnetNetwork(selectedNetwork) ? (
                        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
                            Mainnet selected. This uses real gas and real assets. Use a testnet for testing.
                        </div>
                    ) : null}

                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
                        <div className="font-semibold text-slate-900">Compiled Version</div>
                        <div className="mt-1 break-all">Project: {renderSafeText(projectId)}</div>
                        <div className="break-all">Version: {renderSafeText(customVersionId)}</div>
                        <div>Starter template: {renderSafeText(getCompileStarterTemplate(compileResult, tokenType))}</div>
                        <div>Detected type: {renderSafeText(getCompileDisplayType(compileResult))}</div>
                    </div>
                    {getCompileCapabilityLabels(compileResult).length ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {getCompileCapabilityLabels(compileResult).map((label) => (
                                <span
                                    key={label}
                                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div>
                    <div className="flex items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-slate-700">
                            Constructor Args JSON
                        </label>

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={resetRecommendedArgs}
                                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                            >
                                Use recommended
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    setArgsTouched(true);
                                    setConstructorArgsText("[]");
                                    setPreflightResult(null);
                                    setPreflightStatus("");
                                    setPreflightError("");
                                    setDeployStatus("");
                                    setDeployError("");
                                    setDeployResult(null);
                                }}
                                className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                            >
                                Use empty args
                            </button>
                        </div>
                    </div>

                    <textarea
                        value={constructorArgsText}
                        onChange={(e) => {
                            setArgsTouched(true);
                            setConstructorArgsText(e.target.value);
                            setPreflightResult(null);
                            setPreflightStatus("");
                            setPreflightError("");
                        }}
                        rows={7}
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
                    />

                    <p className="mt-2 text-xs text-slate-500">
                        Recommended args are generated from Studio selections and current wallet.
                        Edit only if you changed the constructor in Solidity.
                    </p>
                </div>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-sm font-semibold text-slate-900">
                    Recommended constructor args
                </div>

                <div className="mt-1 text-xs font-medium text-slate-600">
                    {describeConstructorArgs(recommendedArgs)}
                </div>

                <div className="mt-3">
                    <JsonPreview value={recommendedArgs} />
                </div>
            </div>

            {!isAuthed ? (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
                    Connect wallet and sign in from the top bar before deploying.
                </div>
            ) : null}
            {isAuthed && deploymentLimitReached ? (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                    <div className="font-semibold">Deployment limit reached</div>
                    <div className="mt-1">
                        {deploymentLimitMessage}{" "}
                        <Link to="/upgrade" className="font-semibold underline underline-offset-4">
                            View plans
                        </Link>
                    </div>
                </div>
            ) : null}

            {isAuthed && preflightLimitReached ? (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-800">
                    <div className="font-semibold">Preflight limit reached</div>
                    <div className="mt-1">
                        {preflightLimitMessage}{" "}
                        <Link to="/upgrade" className="font-semibold underline underline-offset-4">
                            View plans
                        </Link>
                    </div>
                </div>
            ) : null}
            <div className="mt-5 overflow-hidden rounded-[1.5rem] border border-[#E8D7AD] bg-gradient-to-br from-[#FFF9EA] via-white to-[#F8FAFC] shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
                <div className="flex flex-col gap-4 border-b border-[#E8D7AD]/70 p-5 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <div className="inline-flex rounded-full border border-[#E8D7AD] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7226]">
                            Preflight simulation
                        </div>

                        <h3 className="mt-3 text-lg font-semibold tracking-[-0.02em] text-[#050713]">
                            Review before wallet signing
                        </h3>

                        <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                            Krionex checks the compiled contract, constructor arguments, selected network, and estimated deployment cost before your wallet opens. This helps you catch issues before signing.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handlePreflightCustom}
                        disabled={
                            preflightLoading ||
                            restoreLoading ||
                            accountUsageLoading ||
                            preflightLimitReached ||
                            !isAuthed ||
                            !customVersionId ||
                            !network ||
                            deployBlocked
                        }
                        className="rounded-2xl bg-[#050713] px-5 py-3 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(15,23,42,0.16)] transition hover:-translate-y-0.5 hover:bg-black disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 disabled:shadow-none"
                    >
                        {preflightLoading
                            ? "Running preflight…"
                            : accountUsageLoading
                                ? "Checking Usage…"
                                : preflightLimitReached
                                    ? "Preflight Limit Reached"
                                    : !network
                                        ? "Select Network First"
                                        : preflightOk
                                            ? "Run Preflight Again"
                                            : "Run Safety Preflight"}
                    </button>
                    <div className="text-xs leading-5 text-slate-500 lg:max-w-[220px] lg:text-right">
                        {preflightLimitReached
                            ? preflightLimitMessage
                            : preflightCreditNotice}
                    </div>
                </div>

                <div className="p-5">
                    {preflightStatus ? (
                        <div
                            className={`rounded-2xl border p-4 text-sm font-semibold ${preflightOk
                                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                : "border-blue-200 bg-blue-50 text-blue-800"
                                }`}
                        >
                            {renderSafeText(preflightStatus)}
                        </div>
                    ) : null}

                    {preflightError ? (
                        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                            {renderSafeText(preflightError)}
                        </div>
                    ) : null}

                    {preflightResult ? (
                        <div className="mt-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs leading-5 text-blue-800">
                            The estimate below is for review only. Your wallet will show the final network fee before you approve the transaction.
                        </div>
                    ) : null}

                    {preflightResult ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                    Network
                                </div>
                                <div className="mt-2 text-sm font-semibold text-slate-950">
                                    {renderSafeText(preflightResult.network)}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    Chain {renderSafeText(preflightResult.chainId)}
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                    Gas Estimate
                                </div>
                                <div className="mt-2 text-sm font-semibold text-slate-950">
                                    {formatGasUnits(preflightResult.gasEstimate)}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    estimated units
                                </div>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                    Estimated Fee Range
                                </div>
                                <div className="mt-2 text-sm font-semibold text-slate-950">
                                    {formatNativeRange(preflightResult?.gas)}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    {formatUsdRange(preflightResult?.gas)}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    Final fee may vary by wallet speed and network conditions
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                                    Bytecode Size
                                </div>
                                <div className="mt-2 text-sm font-semibold text-slate-950">
                                    {formatGasUnits(preflightResult?.txPreview?.dataSizeBytes)} bytes
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    deployment data
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {preflightResult ? (
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <div className="text-sm font-semibold text-slate-950">
                                        Deployment readiness
                                    </div>
                                    <p className="mt-1 text-xs leading-5 text-slate-500">
                                        Deployment is unlocked for this compiled version, constructor input, and selected network.
                                    </p>
                                </div>

                                <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                                    Passed
                                </span>
                            </div>

                            <details className="mt-4">
                                <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                                    Advanced preflight JSON
                                </summary>
                                <pre className="mt-3 max-h-64 overflow-auto rounded-xl bg-slate-950 p-3 text-xs text-slate-100">
                                    {JSON.stringify(preflightResult, null, 2)}
                                </pre>
                            </details>
                        </div>
                    ) : null}
                </div>
            </div>
            {deployError ? (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {renderSafeText(deployError)}
                </div>
            ) : null}

            {deployStatus ? (
                <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm font-medium text-blue-800">
                    {renderSafeText(deployStatus)}
                </div>
            ) : null}

            {deployLoading || deployStatus || deployError ? (
                <button
                    type="button"
                    onClick={resetDeployState}
                    className="mt-3 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700"
                >
                    Reset Deployment Status
                </button>
            ) : null}

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs leading-5 text-slate-600">
                <div className="font-semibold text-slate-900">Deployment usage</div>
                <div className="mt-1">{deployCreditNotice}</div>
                {!preflightOk ? (
                    <div className="mt-1 font-semibold text-amber-700">
                        Deploy stays locked until the preflight passes.
                    </div>
                ) : null}
            </div>

            <button
                type="button"
                onClick={handleDeployCustom}
                disabled={
                    deployLoading ||
                    restoreLoading ||
                    accountUsageLoading ||
                    deploymentLimitReached ||
                    !isAuthed ||
                    !customVersionId ||
                    !network ||
                    deployBlocked ||
                    !preflightOk
                }
                className="mt-5 w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
            >
                {!isAuthed
                    ? "Connect Wallet in Header to Deploy"
                    : accountUsageLoading
                        ? "Checking Usage..."
                        : deploymentLimitReached
                            ? "Deployment Limit Reached"
                            : !network
                                ? "Select Network to Deploy"
                                : deployBlocked
                                    ? "Deployment Blocked by Security Warnings"
                                    : !preflightOk
                                        ? "Deploy Locked — Run Preflight First"
                                        : deployLoading
                                            ? "Deploying Custom Contract..."
                                            : "Deploy Custom Contract"}
            </button>
            {deployResult ? (
                <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <div className="text-sm font-semibold text-emerald-900">
                                Custom deployment confirmed
                            </div>
                            <p className="mt-1 text-xs leading-5 text-emerald-800">
                                The contract was deployed with wallet signing. Krionex automatically submits source verification using the stored compiler input.
                            </p>
                        </div>

                        {deployResult.explorerUrl ? (
                            <a
                                href={deployResult.explorerUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-xl border border-emerald-300 bg-white px-4 py-2 text-center text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                            >
                                Open Explorer
                            </a>
                        ) : null}
                    </div>

                    <div className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
                        <div className="rounded-xl border border-emerald-200 bg-white/80 p-3">
                            <div className="font-semibold text-emerald-950">Contract</div>
                            <div className="mt-1 break-all text-emerald-800">
                                {renderSafeText(deployResult.contractAddress || deployResult.address)}
                            </div>
                        </div>

                        <div className="rounded-xl border border-emerald-200 bg-white/80 p-3">
                            <div className="font-semibold text-emerald-950">Network</div>
                            <div className="mt-1 text-emerald-800">
                                {renderSafeText(deployResult.network)} / Chain {renderSafeText(deployResult.chainId)}
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="text-sm font-semibold text-slate-950">
                                    Source Verification
                                </div>
                                <p className="mt-1 text-xs leading-5 text-slate-500">
                                    Uses the stored standard-input JSON from your compiled custom version. No manual source upload.
                                </p>
                            </div>

                            <div className="flex flex-col gap-2 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={handleRequestVerification}
                                    disabled={verifyLoading || isVerifiedResponse(verifyResult)}
                                    className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                                >
                                    {verifyLoading
                                        ? "Working…"
                                        : isVerifiedResponse(verifyResult)
                                            ? "Verified"
                                            : verifyResult
                                                ? "Retry Verification"
                                                : "Verify Contract"}
                                </button>

                                <button
                                    type="button"
                                    onClick={handleCheckVerificationStatus}
                                    disabled={
                                        verifyLoading ||
                                        !(
                                            verifyResult?.guid ||
                                            verifyResult?.verification?.guid ||
                                            verifyResult?.status === "pending" ||
                                            verifyResult?.status === "queued" ||
                                            verifyResult?.status === "submitted"
                                        )
                                    }
                                    className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    Check Status
                                </button>
                            </div>
                        </div>

                        {verifyStatus ? (
                            <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs font-medium text-blue-800">
                                {renderSafeText(verifyStatus)}
                            </div>
                        ) : null}

                        {verifyError ? (
                            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-medium text-red-700">
                                {renderSafeText(verifyError)}
                            </div>
                        ) : null}

                        {verifyResult ? (
                            <pre className="mt-3 max-h-56 overflow-auto rounded-xl bg-slate-950 p-3 text-xs text-slate-100">
                                {JSON.stringify(verifyResult, null, 2)}
                            </pre>
                        ) : null}
                    </div>

                    <details className="mt-4">
                        <summary className="cursor-pointer text-xs font-semibold text-emerald-900">
                            View deployment JSON
                        </summary>
                        <pre className="mt-3 max-h-72 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-100">
                            {JSON.stringify(deployResult, null, 2)}
                        </pre>
                    </details>
                </div>
            ) : null}

            <p className="mt-4 text-xs leading-5 text-slate-500">
                Security: Krionex prepares an unsigned transaction for review. Your wallet signs and sends it, and Krionex records the confirmed on-chain deployment.
            </p>
        </div>
    );
}